import { Controller } from "@hotwired/stimulus";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";

const DAY_FORMAT = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric" });
const TIME_FORMAT = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" });

// "Block online orders" modal content. One inline calendar that is a day picker: every
// tap picks that day. A range is opt-in: "Block through a later day" arms the next tap
// as the end. Flatpickr runs in single mode (no hover trail, none of its range classes)
// and the range itself is ours: `start`/`end` here, painted in onDayCreate. Hours are optional and sit behind "Set hours"; on a range they repeat
// each day. Existing blocks come in as hidden
// `block` targets: their days are slashed and can't be picked, and tapping one turns
// the same form into that block's editor (its range selected, its hours filled, PATCH).
// Every form posts with fetch; the server answers with this whole element re-rendered,
// which replaces us in place so the modal stays open showing the change. The page
// underneath is stale after that, so it reloads once the modal closes.
export default class extends Controller {
  static targets = [
    "calendar", "form", "method", "startsOn", "endsOn", "startsAt", "endsAt",
    "hoursToggle", "hoursFields", "summary", "range", "submit", "block", "pause", "title",
  ];
  static values = { today: String, maxDays: Number, notice: String };

  connect() {
    this.modal = this.element.closest(".modal");
    this.createUrl = this.formTarget.action;
    this.editing = null;
    this.start = null;
    this.end = null;
    this.ranging = false;
    // Flatpickr mounts an inline calendar as a sibling of its element; appendTo keeps it
    // inside the target so the wrapper can centre it.
    this.picker = flatpickr(this.calendarTarget, {
      inline: true,
      appendTo: this.calendarTarget,
      mode: "single",
      dateFormat: "Y-m-d",
      minDate: this.todayValue,
      disable: this.taken(),
      disableMobile: true,
      onChange: ([date]) => this.pick(date),
      onDayCreate: (_dates, _str, _fp, dayElem) => this.decorate(dayElem),
    });
    // Bootstrap's show can land before the modal has a size.
    this.redraw = () => this.picker.redraw();
    this.modal.addEventListener("shown.bs.modal", this.redraw);
    this.reload = () => {
      if (this.modal.dataset.changed) window.location.reload();
    };
    this.modal.addEventListener("hidden.bs.modal", this.reload);
    // Escape with a time popup open closes the popup, not the modal underneath it.
    // Capture on document runs before Bootstrap's own keydown listener on the modal.
    this.escape = (event) => {
      if (event.key !== "Escape" || !this.element.contains(event.target)) return;
      const open = this.timePickers.filter((picker) => picker.isOpen);
      if (open.length === 0) return;
      event.stopPropagation();
      open.forEach((picker) => picker.close());
      // Closing the popup drops focus to body; a second Escape must still reach the modal.
      this.modal.focus();
    };
    document.addEventListener("keydown", this.escape, true);
    // Same widget for the hours as for the days. The field keeps "17:00" for the server;
    // the visible input shows "5:00 PM".
    this.timePickers = [this.startsAtTarget, this.endsAtTarget].map((input) =>
      flatpickr(input, {
        enableTime: true,
        noCalendar: true,
        dateFormat: "H:i",
        altInput: true,
        altFormat: "h:i K",
        minuteIncrement: 15,
        // Inside the modal, not on body: Bootstrap's focus trap drags focus off a popup
        // it doesn't contain, landing it on the close button.
        static: true,
        // The open animation keyframes set transform, which would shove a popup centred by
        // CSS aside for its 300ms.
        animate: false,
        disableMobile: true,
        onChange: () => this.sync(),
      })
    );
    const today = this.blockFor(this.todayValue);
    if (today) this.edit(today);
    else this.startNew();
    if (this.noticeValue) this.say(this.noticeValue, "order-block__summary--done");
  }

  disconnect() {
    this.modal.removeEventListener("shown.bs.modal", this.redraw);
    this.modal.removeEventListener("hidden.bs.modal", this.reload);
    document.removeEventListener("keydown", this.escape, true);
    this.timePickers.forEach((picker) => picker.destroy());
    if (this.picker) this.picker.destroy();
  }

  // Posts the form itself and swaps in what comes back. rails-ujs has already asked the
  // data-confirm question by the time submit fires. A 422 carries only the message.
  async submit(event) {
    event.preventDefault();
    const form = event.target;
    const button = form.querySelector("button[type=submit], button:not([type])");
    if (button) button.disabled = true;
    try {
      // The page token, not the form's own: a per-form token is bound to the create URL
      // and fails once edit() points the form at a block's PATCH URL.
      const token = document.querySelector("meta[name=csrf-token]")?.content;
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest", Accept: "text/html", "X-CSRF-Token": token },
      });
      const body = await response.text();
      if (response.redirected) {
        // fetch follows the sign-in redirect and lands on a 200 login page.
        this.say("Your session has expired. Reload the page and sign in again.", "order-block__summary--error");
        return;
      }
      if (response.ok) {
        this.modal.dataset.changed = "true";
        this.element.outerHTML = body;
        return;
      }
      const plain = response.headers.get("Content-Type")?.startsWith("text/plain");
      this.say(response.status === 422 && plain ? body : "Something went wrong. Try again.", "order-block__summary--error");
    } catch {
      this.say("Something went wrong. Try again.", "order-block__summary--error");
    }
    if (button) button.disabled = false;
  }

  // Back to adding a block: nothing selected, every existing block slashed.
  startNew() {
    this.editing = null;
    this.ranging = false;
    this.blockTargets.forEach((block) => (block.hidden = true));
    if (this.hasPauseTarget) this.pauseTarget.hidden = false;
    this.formTarget.action = this.createUrl;
    this.methodTarget.disabled = true;
    this.titleTarget.textContent = "Block online orders";
    this.submitTarget.textContent = "Block orders";
    this.picker.set("minDate", this.todayValue);
    this.picker.set("disable", this.taken());
    this.select(null, null);
    this.closeHours();
  }

  // The form becomes this block's editor. Its own days open up on the calendar, and a
  // start already behind us stays reachable so the end can still be moved.
  edit(block) {
    this.editing = block;
    this.ranging = false;
    this.blockTargets.forEach((other) => (other.hidden = other !== block));
    if (this.hasPauseTarget) this.pauseTarget.hidden = true;
    this.formTarget.action = block.dataset.url;
    this.methodTarget.disabled = false;
    this.titleTarget.textContent = block.dataset.today === "true" ? "Blocked today" : "Edit order block";
    this.submitTarget.textContent = "Save changes";
    this.picker.set("minDate", block.dataset.from < this.todayValue ? block.dataset.from : this.todayValue);
    this.picker.set("disable", this.taken());
    const from = this.parse(block.dataset.from);
    this.select(from, block.dataset.to === block.dataset.from ? null : this.parse(block.dataset.to));
    this.picker.jumpToDate(from);
    if (block.dataset.startsAt) this.showHours(block.dataset.startsAt, block.dataset.endsAt);
    else this.closeHours();
  }

  // A tap picks that day. Armed by range(), a tap on a later day closes the range instead.
  // While editing, a tap outside the block drops the edit and starts fresh.
  pick(date) {
    if (this.editing && !this.contains(this.editing, this.iso(date))) this.startNew();
    if (this.ranging && this.start && date > this.start) this.select(this.start, date);
    else this.select(date, null);
    this.ranging = this.ranging && !this.end;
    this.sync();
  }

  range() {
    this.ranging = true;
    this.sync();
  }

  contains({ dataset }, iso) {
    return iso >= dataset.from && iso <= dataset.to;
  }

  // Flatpickr holds the start (its `selected` fill); the end and the days between are
  // painted by decorate() on redraw. The property is set directly: setDate would jump the
  // view back to the start's month after an end tap in the next one.
  select(start, end) {
    this.start = start;
    this.end = end;
    this.picker.selectedDates = start ? [start] : [];
    this.picker.redraw();
  }

  // One day submits as a single-day block; a start and an end as the range between.
  sync() {
    const start = this.start;
    const end = this.end || start;
    const days = start ? Math.round((end - start) / 86400000) + 1 : 0;
    const hours = this.hours();
    const crossed = start ? this.crossed(start, end) : null;
    // A block under way keeps its past start; any other past day is the server's "can't
    // be in the past", so the button never offers it.
    const passed = !!start && this.iso(start) < this.todayValue && this.iso(start) !== this.editing?.dataset.from;
    const allowed = !!start && !passed && days <= this.maxDaysValue && hours !== null && !crossed;
    this.startsOnTarget.value = start ? this.iso(start) : "";
    this.endsOnTarget.value = allowed ? this.iso(end) : "";
    this.submitTarget.disabled = !allowed;
    // The chip shares its row with the time fields, so it steps aside while they are open.
    this.rangeTarget.hidden = !(start && !this.end && !this.ranging && !this.editing && this.hoursFieldsTarget.hidden);
    const today = this.editing?.dataset.today === "true";
    this.say(this.sentence(start, end, days, hours, crossed, passed), today ? "order-block__summary--today" : "");
  }

  // The block (other than the one being edited) a start..end would run over, if any.
  crossed(start, end) {
    const from = this.iso(start);
    const to = this.iso(end);
    return this.blockTargets.find(
      (block) => block !== this.editing && block.dataset.from <= to && block.dataset.to >= from
    );
  }

  // Any day can be picked (Christmas from September); only a range's length is capped,
  // mirroring OrderOverride::MAX_RANGE_DAYS on the server.
  sentence(start, end, days, hours, crossed, passed) {
    if (!start) return "Pick a day to block.";
    if (passed) return `${this.label(start)} has passed. Pick today or a later day.`;
    if (hours === null) return "Set a start and a later end time, or switch back to all day.";
    if (days > this.maxDaysValue) return `That's ${days} days. A block can cover up to ${this.maxDaysValue}.`;
    if (crossed) return `Those days run over a block already set (${this.blockLabel(crossed)}). Tap it to edit it.`;
    const single = this.iso(start) === this.iso(end);
    if (single && this.ranging) return `${this.label(start)}. Now tap the last day.`;
    if (single) return `${this.label(start)}${hours ? `, ${hours}` : ""}`;
    return `${this.label(start)} – ${this.label(end)} (${days} days)${hours ? `, ${hours} each day` : ""}`;
  }

  // The summary is the one loud line: what the button will do, a saved change, or an error.
  say(text, tone = "") {
    this.summaryTarget.textContent = text;
    this.summaryTarget.className = `order-block__summary ${tone}`.trim();
  }

  blockLabel({ dataset }) {
    const from = this.label(this.parse(dataset.from));
    return dataset.from === dataset.to ? from : `${from} – ${this.label(this.parse(dataset.to))}`;
  }

  // "" for all day, the formatted window when both times are set, null while half done
  // or with the end not after the start (a window can't cross midnight).
  hours() {
    if (this.hoursFieldsTarget.hidden) return "";
    const from = this.startsAtTarget.value;
    const to = this.endsAtTarget.value;
    if (!from && !to) return "";
    if (!from || !to || to <= from) return null;
    return `${this.time(from)} – ${this.time(to)}`;
  }

  // Starts from a visible window rather than blank fields: the summary reads it back at
  // once and the button stays live, so the hours are one adjustment away, never a chore.
  openHours() {
    this.showHours("12:00", "14:00");
  }

  showHours(from, to) {
    this.hoursToggleTarget.hidden = true;
    this.hoursFieldsTarget.hidden = false;
    this.timePickers[0].setDate(from);
    this.timePickers[1].setDate(to);
    this.sync();
  }

  closeHours() {
    this.timePickers.forEach((picker) => picker.clear());
    this.hoursFieldsTarget.hidden = true;
    this.hoursToggleTarget.hidden = false;
    this.sync();
  }

  // Paints what flatpickr's single mode doesn't know about: the picked range, and the
  // already-blocked days, slashed, which tap through to that block. No tooltip: the
  // cell is rebuilt on every redraw, which strands a Bootstrap tooltip mid-hover.
  decorate(dayElem) {
    const iso = this.iso(dayElem.dateObj);
    if (this.end) {
      const from = this.iso(this.start);
      const to = this.iso(this.end);
      if (iso === from) dayElem.classList.add("order-block-start");
      else if (iso === to) dayElem.classList.add("order-block-end");
      else if (iso > from && iso < to) dayElem.classList.add("order-block-in");
    }
    const block = this.blockFor(iso);
    if (!block) return;

    dayElem.classList.add("order-block-taken");
    dayElem.setAttribute("aria-label", `${this.label(dayElem.dateObj)}, already blocked. Tap to edit it.`);
    dayElem.addEventListener("click", () => this.edit(block));
  }

  // Blocks other than the one being edited, as flatpickr disable ranges.
  taken() {
    return this.blockTargets
      .filter((block) => block !== this.editing)
      .map(({ dataset }) => ({ from: dataset.from, to: dataset.to }));
  }

  blockFor(iso) {
    return this.blockTargets.find((block) => block !== this.editing && this.contains(block, iso));
  }

  label(date) {
    return DAY_FORMAT.format(date);
  }

  // "17:00" from a time input → "5:00 PM".
  time(value) {
    const [hours, minutes] = value.split(":").map(Number);
    return TIME_FORMAT.format(new Date(2000, 0, 1, hours, minutes));
  }

  iso(date) {
    return flatpickr.formatDate(date, "Y-m-d");
  }

  // "2026-09-10" → local midnight, the same instant flatpickr gives a day cell.
  parse(iso) {
    return flatpickr.parseDate(iso, "Y-m-d");
  }
}
